import React, { useState, useEffect, useContext, useRef } from 'react';
import { db } from '../utils/firebaseConfig';
import { logUserActivity } from '../utils/auditLogger';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  FiUser,
  FiUsers,
  FiUserCheck,
  FiUserMinus,
} from 'react-icons/fi';
import { MdHistory } from 'react-icons/md';
import AssignmentHistory from './AssignmentHistory';
import ConfirmationModal from './ConfirmationModal';
import { AuthContext } from '../contexts/AuthContext';

// Import pdfMake and fonts
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { differenceInSeconds, format } from 'date-fns';
import { pdfStyles } from '../utils/pdfStyles';
import { getBase64ImageFromURL } from '../utils/pdfUtils';
pdfMake.vfs = pdfFonts.vfs;

const AssignTrainee = ({ onViewHistory }) => {
  const [trainees, setTrainees] = useState([]);
  const [trainersAndAdmins, setTrainersAndAdmins] = useState([]);
  const [selectedTrainee, setSelectedTrainee] = useState('');
  const [selectedTrainerOrAdmin, setSelectedTrainerOrAdmin] = useState('');
  const [currentAssignments, setCurrentAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAssignmentHistory, setShowAssignmentHistory] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });
  const modalRef = useRef(null);

  // Add AuthContext
  const { currentUser, userName } = useContext(AuthContext);

  useEffect(() => {
    const traineeQuery = query(
      collection(db, 'users'),
      where('role', '==', 'trainee'),
      where('archived', '==', false),
      where('active', '==', true)
    );

    const trainerAdminQuery = query(
      collection(db, 'users'),
      where('role', 'in', ['trainer', 'admin']),
      where('archived', '==', false),
      where('active', '==', true)
    );

    const unsubscribeTrainees = onSnapshot(traineeQuery, (snapshot) => {
      const traineeData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTrainees(traineeData);
    });

    const unsubscribeTrainers = onSnapshot(trainerAdminQuery, (snapshot) => {
      const trainerAdminData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTrainersAndAdmins(trainerAdminData);
    });

    return () => {
      unsubscribeTrainees();
      unsubscribeTrainers();
    };
  }, []);

  // New useEffect to update current assignments when trainees or trainers change
  useEffect(() => {
    updateCurrentAssignments(trainees, trainersAndAdmins);
  }, [trainees, trainersAndAdmins]);

  const updateCurrentAssignments = (updatedTrainees, updatedTrainers) => {
    const assignments = {};
    updatedTrainees.forEach((trainee) => {
      if (trainee.assignedTrainers && trainee.assignedTrainers.length > 0) {
        const assignedTo = trainee.assignedTrainers
          .map((trainerId) => {
            const trainer = updatedTrainers.find((t) => t.id === trainerId);
            return trainer ? `${trainer.name} (FTO)` : 'Unknown';
          })
          .join(', ');
        assignments[trainee.id] = assignedTo;
      } else {
        assignments[trainee.id] = 'Not currently assigned';
      }
    });
    setCurrentAssignments(assignments);
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    
    // Get the trainer's name from trainersAndAdmins array
    const selectedTrainer = trainersAndAdmins.find(t => t.id === selectedTrainerOrAdmin);
    const trainerName = selectedTrainer ? selectedTrainer.name : 'Unknown Trainer';

    setConfirmModal({
      isOpen: true,
      title: 'Confirm Assignment',
      message: `Are you sure you want to assign this trainee to ${trainerName}?`,
      onConfirm: async () => {
        if (!selectedTrainee || !selectedTrainerOrAdmin) {
          toast.error('Please select both a trainee and a trainer/admin.');
          return;
        }

        try {
          // Fetch latest trainee data
          const traineeRef = doc(db, 'users', selectedTrainee);
          const traineeDoc = await getDoc(traineeRef);

          if (!traineeDoc.exists()) {
            toast.error('Selected trainee does not exist.');
            return;
          }

          const traineeData = traineeDoc.data();

          // Check if trainee is still active and not archived
          if (!traineeData.active || traineeData.archived) {
            toast.error('Selected trainee is no longer active.');
            return;
          }

          // Fetch latest trainer/admin data
          const trainerRef = doc(db, 'users', selectedTrainerOrAdmin);
          const trainerDoc = await getDoc(trainerRef);

          if (!trainerDoc.exists()) {
            toast.error('Selected trainer/admin does not exist.');
            return;
          }

          const trainerData = trainerDoc.data();

          // Check if trainer/admin is still active and not archived
          if (!trainerData.active || trainerData.archived) {
            toast.error('Selected trainer/admin is no longer active.');
            return;
          }

          // Initialize assignedTrainers if not present
          let assignedTrainers = traineeData.assignedTrainers || [];

          // Check if trainer/admin is already assigned
          if (assignedTrainers.includes(selectedTrainerOrAdmin)) {
            toast.error('This trainer/admin is already assigned to the trainee.');
            return;
          }

          // Add the trainer/admin to the assignedTrainers array
          assignedTrainers.push(selectedTrainerOrAdmin);

          // Update the trainee document
          await updateDoc(traineeRef, {
            assignedTrainers: assignedTrainers,
          });

          // Record new assignment in history
          await addDoc(collection(db, 'assignmentHistory'), {
            traineeId: selectedTrainee,
            trainerId: selectedTrainerOrAdmin,
            startDate: serverTimestamp(),
            endDate: null,
          });

          // Audit log: assignment
          await logUserActivity(
            currentUser?.uid || 'unknown',
            userName || currentUser?.displayName || 'unknown',
            'ASSIGN_TRAINEE',
            { traineeId: selectedTrainee, trainerId: selectedTrainerOrAdmin }
          );

          toast.success('FTO assigned to trainee successfully!');
          setSelectedTrainerOrAdmin('');
        } catch (error) {
          console.error('Error assigning trainee:', error);
          toast.error('Failed to assign trainer/admin. Please try again.');
        }
      }
    });

    // Ensure modal is visible after it opens with a longer timeout
    setTimeout(() => {
      if (modalRef.current) {
        const viewportHeight = window.innerHeight;
        const modalRect = modalRef.current.getBoundingClientRect();
        
        if (modalRect.top < 0 || modalRect.bottom > viewportHeight) {
          window.scrollTo({
            top: modalRect.top + window.scrollY - (viewportHeight - modalRect.height) / 2,
            behavior: 'smooth'
          });
        }
      }
    }, 300);
  };

  const handleUnassign = async () => {
    if (!selectedTrainee || !selectedTrainerOrAdmin) {
      toast.error('Please select both a trainee and a trainer/admin to unassign.');
      return;
    }

    try {
      // Fetch latest trainee data
      const traineeRef = doc(db, 'users', selectedTrainee);
      const traineeDoc = await getDoc(traineeRef);

      if (!traineeDoc.exists()) {
        toast.error('Selected trainee does not exist.');
        return;
      }

      const traineeData = traineeDoc.data();

      // Check if trainee is still active and not archived
      if (!traineeData.active || traineeData.archived) {
        toast.error('Selected trainee is no longer active.');
        return;
      }

      // Fetch latest trainer/admin data
      const trainerRef = doc(db, 'users', selectedTrainerOrAdmin);
      const trainerDoc = await getDoc(trainerRef);

      if (!trainerDoc.exists()) {
        toast.error('Selected trainer/admin does not exist.');
        return;
      }

      const trainerData = trainerDoc.data();

      // Check if trainer/admin is still active and not archived
      if (!trainerData.active || trainerData.archived) {
        toast.error('Selected FTO is no longer active.');
        return;
      }

      let assignedTrainers = traineeData.assignedTrainers || [];

      if (!assignedTrainers.includes(selectedTrainerOrAdmin)) {
        toast.error('This FTO is not currently assigned to the trainee.');
        return;
      }

      // Remove the trainer/admin from the assignedTrainers array
      assignedTrainers = assignedTrainers.filter(
        (id) => id !== selectedTrainerOrAdmin
      );

      // Update the trainee document
      await updateDoc(traineeRef, {
        assignedTrainers: assignedTrainers,
      });

      // End current assignment in history
      // Find the assignmentHistory document where traineeId and trainerId match and endDate is null
      const assignmentHistoryQuery = query(
        collection(db, 'assignmentHistory'),
        where('traineeId', '==', selectedTrainee),
        where('trainerId', '==', selectedTrainerOrAdmin),
        where('endDate', '==', null)
      );

      const assignmentSnapshot = await getDocs(assignmentHistoryQuery);

      if (!assignmentSnapshot.empty) {
        const assignmentDoc = assignmentSnapshot.docs[0];
        await updateDoc(assignmentDoc.ref, {
          endDate: serverTimestamp(),
        });
      }

      toast.success('Trainer/Admin unassigned from trainee successfully!');
      // Audit log: unassignment
      await logUserActivity(
        currentUser?.uid || 'unknown',
        userName || currentUser?.displayName || 'unknown',
        'UNASSIGN_TRAINEE',
        { traineeId: selectedTrainee, trainerId: selectedTrainerOrAdmin }
      );
      setSelectedTrainerOrAdmin('');
    } catch (error) {
      console.error('Error unassigning trainer/admin:', error);
      toast.error('Failed to unassign trainer/admin. Please try again.');
    }
  };

  // Function to handle downloading assignment history
  const handleDownloadAssignmentHistory = async () => {
    if (!selectedTrainee) {
      toast.error('Please select a trainee to download assignment history.');
      return;
    }

    setLoading(true);

    try {
      // Fetch assignment history data
      const assignments = await fetchAssignmentsForPDF(selectedTrainee);

      if (assignments.length === 0) {
        toast.info('No assignment history found for the selected trainee.');
        setLoading(false);
        return;
      }

      // Generate PDF
      await generateAssignmentHistoryPDF(assignments);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate assignment history PDF.');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch assignments for the selected trainee
  const fetchAssignmentsForPDF = async (traineeId) => {
    const assignments = [];
    const q = query(
      collection(db, 'assignmentHistory'),
      where('traineeId', '==', traineeId),
      orderBy('startDate', 'desc')
    );
    const snapshot = await getDocs(q);

    snapshot.forEach((doc) => {
      const data = doc.data();
      assignments.push({
        id: doc.id,
        ...data,
        startDate: data.startDate ? data.startDate.toDate() : null,
        endDate: data.endDate ? data.endDate.toDate() : null,
      });
    });

    // Fetch trainer names
    const trainerIds = [...new Set(assignments.map((a) => a.trainerId))];
    const trainerNames = {};
    for (const trainerId of trainerIds) {
      const trainerDoc = await getDoc(doc(db, 'users', trainerId));
      if (trainerDoc.exists()) {
        trainerNames[trainerId] = trainerDoc.data().name;
      } else {
        trainerNames[trainerId] = 'Unknown';
      }
    }

    // Add trainer names to assignments
    assignments.forEach((assignment) => {
      assignment.trainerName = trainerNames[assignment.trainerId] || 'Unknown';
    });

    // Calculate durations
    const now = new Date();
    assignments.forEach((assignment) => {
      const { startDate, endDate } = assignment;
      if (startDate) {
        const end = endDate || now;
        const totalSeconds = differenceInSeconds(end, startDate);

        const days = Math.floor(totalSeconds / (24 * 3600));
        const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        assignment.duration = `${days}d ${hours}h ${minutes}m`;
      } else {
        assignment.duration = 'N/A';
      }
    });

    return assignments;
  };

  // Function to generate the PDF using pdfMake
  const generateAssignmentHistoryPDF = async (assignments) => {
    const traineeName =
      trainees.find((t) => t.id === selectedTrainee)?.name || 'Unknown Trainee';
    const traineeIdNumber =
      trainees.find((t) => t.id === selectedTrainee)?.idNumber || '';

    const content = [];

    // PDF Colors matching DOR style
    const PDF_COLORS = {
      primary: '#0A4DA2',
      secondary: '#3b82f6',
      accent: '#93c5fd',
      success: '#16a34a',
      warning: '#d97706',
      danger: '#dc2626',
      tableHeader: '#E2E8F0',
      tableAlt: '#f8fafc',
      text: '#1e293b',
      lightText: '#64748b',
      border: '#cbd5e1',
      headerBg: '#dbeafe',
    };

    // Banner image
    try {
      const banner = await getBase64ImageFromURL('/banner.png');
      content.push({ 
        image: banner, 
        width: 515, 
        alignment: 'center', 
        margin: [0, 0, 0, 10] 
      });
    } catch {
      content.push({
        text: 'FIELD TRAINING PROGRAM',
        fontSize: 26,
        bold: true,
        color: PDF_COLORS.primary,
        alignment: 'center',
        margin: [0, 0, 0, 10]
      });
    }

    // Title block with line
    content.push(
      { 
        text: 'FTO Assignment History Report', 
        fontSize: 24,
        bold: true,
        color: PDF_COLORS.primary,
        alignment: 'center',
        margin: [0, 0, 0, 8]
      },
      { 
        canvas: [
          {
            type: 'line',
            x1: 0, y1: 0,
            x2: 515, y2: 0,
            lineWidth: 2,
            lineColor: PDF_COLORS.secondary,
          }
        ],
        margin: [0, 0, 0, 15]
      }
    );

    // Trainee Info Box
    content.push({
      table: {
        widths: ['*'],
        body: [
          [
            {
              table: {
                widths: ['50%', '50%'],
                body: [
                  [
                    { 
                      text: `Probationary Officer: ${traineeName}${traineeIdNumber ? ` (#${traineeIdNumber})` : ''}`,
                      fontSize: 10,
                      color: PDF_COLORS.text,
                      fillColor: PDF_COLORS.headerBg,
                      margin: [8, 8],
                      border: [true, true, false, true],
                      borderColor: [PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border],
                    },
                    { 
                      text: `Report Date: ${format(new Date(), 'MMMM d, yyyy')}`,
                      fontSize: 10,
                      color: PDF_COLORS.text,
                      fillColor: PDF_COLORS.headerBg,
                      margin: [8, 8],
                      border: [false, true, true, true],
                      borderColor: [PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border, PDF_COLORS.border],
                    }
                  ]
                ]
              },
              layout: {
                hLineWidth: () => 1,
                vLineWidth: () => 1,
                hLineColor: () => PDF_COLORS.border,
                vLineColor: () => PDF_COLORS.border,
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
              }
            }
          ]
        ]
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 20],
    });

    // Calculate total time per FTO for pie chart
    const ftoTimeMap = {};
    let totalMinutes = 0;
    
    assignments.forEach((assignment) => {
      if (assignment.startDate) {
        const end = assignment.endDate || new Date();
        const totalSeconds = differenceInSeconds(end, assignment.startDate);
        const minutes = Math.floor(totalSeconds / 60);
        
        if (!ftoTimeMap[assignment.trainerName]) {
          ftoTimeMap[assignment.trainerName] = 0;
        }
        ftoTimeMap[assignment.trainerName] += minutes;
        totalMinutes += minutes;
      }
    });

    // Create pie chart data
    if (Object.keys(ftoTimeMap).length > 0 && totalMinutes > 0) {
      content.push({
        text: 'Time Distribution by FTO',
        fontSize: 14,
        bold: true,
        color: PDF_COLORS.primary,
        margin: [0, 0, 0, 10]
      });

      // Simple text-based representation of pie chart data
      const pieChartData = Object.entries(ftoTimeMap)
        .sort(([,a], [,b]) => b - a)
        .map(([name, minutes]) => {
          const percentage = ((minutes / totalMinutes) * 100).toFixed(1);
          const days = Math.floor(minutes / (24 * 60));
          const hours = Math.floor((minutes % (24 * 60)) / 60);
          const mins = minutes % 60;
          
          return [
            { text: name, fontSize: 9, color: PDF_COLORS.text },
            { text: `${days}d ${hours}h ${mins}m`, fontSize: 9, color: PDF_COLORS.text, alignment: 'center' },
            { text: `${percentage}%`, fontSize: 9, bold: true, color: PDF_COLORS.primary, alignment: 'right' }
          ];
        });

      content.push({
        table: {
          widths: ['*', 100, 60],
          body: [
            [
              { text: 'FTO Name', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader },
              { text: 'Total Time', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader, alignment: 'center' },
              { text: 'Percentage', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader, alignment: 'right' }
            ],
            ...pieChartData
          ]
        },
        layout: {
          hLineWidth: (i) => i === 0 || i === 1 ? 1 : 0.5,
          vLineWidth: () => 0,
          hLineColor: () => PDF_COLORS.border,
          paddingLeft: () => 8,
          paddingRight: () => 8,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
        margin: [0, 0, 0, 25]
      });
    }

    // Assignment History Table
    content.push({
      text: 'Detailed Assignment History',
      fontSize: 14,
      bold: true,
      color: PDF_COLORS.primary,
      margin: [0, 0, 0, 10]
    });

    const tableBody = [
      [
        { text: 'FTO Name', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader },
        { text: 'Start Date', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader },
        { text: 'End Date', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader },
        { text: 'Duration', style: 'tableHeader', fillColor: PDF_COLORS.tableHeader },
      ],
      ...assignments.map((assignment, index) => {
        const startDate = assignment.startDate
          ? format(assignment.startDate, 'MMM d, yyyy h:mm a')
          : 'Unknown';
        const endDate = assignment.endDate
          ? format(assignment.endDate, 'MMM d, yyyy h:mm a')
          : 'Current Assignment';

        return [
          { text: assignment.trainerName, style: 'tableCell', fillColor: index % 2 === 0 ? 'white' : PDF_COLORS.tableAlt },
          { text: startDate, style: 'tableCell', fillColor: index % 2 === 0 ? 'white' : PDF_COLORS.tableAlt },
          { text: endDate, style: 'tableCell', fillColor: index % 2 === 0 ? 'white' : PDF_COLORS.tableAlt },
          { text: assignment.duration, style: 'tableCell', fillColor: index % 2 === 0 ? 'white' : PDF_COLORS.tableAlt },
        ];
      }),
    ];

    // Add summary row
    const totalDays = Math.floor(totalMinutes / (24 * 60));
    const totalHours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const totalMins = totalMinutes % 60;
    
    tableBody.push([
      { text: 'Total Training Time', bold: true, fontSize: 9, fillColor: PDF_COLORS.headerBg },
      { text: '', fillColor: PDF_COLORS.headerBg },
      { text: '', fillColor: PDF_COLORS.headerBg },
      { text: `${totalDays}d ${totalHours}h ${totalMins}m`, bold: true, fontSize: 9, fillColor: PDF_COLORS.headerBg }
    ]);

    content.push({
      table: { 
        headerRows: 1, 
        widths: ['*', '*', '*', 'auto'], 
        body: tableBody 
      },
      layout: {
        hLineWidth: (i, node) => i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5,
        vLineWidth: () => 0,
        hLineColor: () => PDF_COLORS.border,
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 5,
        paddingBottom: () => 5,
      }
    });

    // Footer signature section
    content.push(
      { text: '', margin: [0, 30, 0, 0] },
      {
        columns: [
          {
            width: '50%',
            stack: [
              {
                canvas: [{
                  type: 'line',
                  x1: 0, y1: 0,
                  x2: 200, y2: 0,
                  lineWidth: 1,
                  lineColor: PDF_COLORS.text,
                  dash: { length: 1 },
                }],
                margin: [0, 20, 0, 5],
              },
              { text: 'FTO Supervisor Signature', fontSize: 9, color: PDF_COLORS.lightText },
              { text: 'Date: _____________________', fontSize: 9, color: PDF_COLORS.lightText, margin: [0, 10, 0, 0] },
            ]
          },
          {
            width: '50%',
            stack: [
              {
                canvas: [{
                  type: 'line',
                  x1: 0, y1: 0,
                  x2: 200, y2: 0,
                  lineWidth: 1,
                  lineColor: PDF_COLORS.text,
                  dash: { length: 1 },
                }],
                margin: [0, 20, 0, 5],
                alignment: 'right',
              },
              { text: 'Command Staff Signature', fontSize: 9, color: PDF_COLORS.lightText, alignment: 'right' },
              { text: 'Date: _____________________', fontSize: 9, color: PDF_COLORS.lightText, alignment: 'right', margin: [0, 10, 0, 0] },
            ]
          }
        ]
      }
    );

    pdfMake
      .createPdf({
        pageMargins: [40, 20, 40, 40],
        footer: function(currentPage, pageCount) {
          return {
            text: `Page ${currentPage} of ${pageCount}`,
            alignment: 'center',
            fontSize: 8,
            margin: [0, 10, 0, 0],
            color: '#6B7280',
          };
        },
        content,
        styles: {
          ...pdfStyles,
          tableHeader: {
            ...pdfStyles.tableHeader,
            color: PDF_COLORS.text,
            bold: true,
            fontSize: 9,
          },
          tableCell: {
            ...pdfStyles.tableCell,
            fontSize: 9,
          }
        },
      })
      .download(`AssignmentHistory_${traineeName}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  // Helper function to format date and time
  const formatDateTime = (date) => {
    return date.toLocaleString(); // You can adjust the format as needed
  };

  // Tailwind CSS styles
  const cardStyle = `p-4 sm:p-6 rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark transition-colors duration-300`;
  const inputStyle = `w-full p-3 rounded-md bg-secondary-light dark:bg-secondary-dark text-text-light dark:text-text-dark border border-secondary-light dark:border-secondary-dark focus:outline-none focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark transition-colors duration-300`;
  const buttonStyle = `w-full px-4 py-2 rounded-md text-white transition-colors duration-300 flex items-center justify-center`;

   // Determine if the selected trainer/admin is assigned to the selected trainee
   const traineeAssignedTrainers =
   trainees.find((t) => t.id === selectedTrainee)?.assignedTrainers || [];

 const isTrainerAssignedToTrainee =
   selectedTrainerOrAdmin && traineeAssignedTrainers.includes(selectedTrainerOrAdmin);

 // Determine if the selected trainee and trainer/admin are active
 const isSelectedTraineeActive =
   trainees.find((t) => t.id === selectedTrainee)?.active;
 const isSelectedTrainerActive =
   trainersAndAdmins.find((t) => t.id === selectedTrainerOrAdmin)?.active;

 const closeConfirmModal = () => {
   setConfirmModal({
     isOpen: false,
     title: '',
     message: '',
     onConfirm: async () => {},
   });
 };

 return (
   <div className={cardStyle}>
     <h2 className="text-2xl font-semibold mb-6 flex items-center">
       <FiUsers className="mr-2" />
       Assign Trainee to FTO
     </h2>
     <div className="mb-4">
       <label className="block mb-2 font-semibold">Select Trainee:</label>
       <select
         className={inputStyle}
         value={selectedTrainee}
         onChange={(e) => {
           setSelectedTrainee(e.target.value);
           setSelectedTrainerOrAdmin(''); // Reset selected trainer/admin when trainee changes
         }}
       >
         <option value="">Choose a trainee</option>
         {trainees.map((trainee) => (
           <option key={trainee.id} value={trainee.id}>
             {trainee.name}
             {currentAssignments[trainee.id] === 'Not currently assigned' 
               ? ' (Unassigned)'
               : currentAssignments[trainee.id] 
                 ? ` (Assigned to ${currentAssignments[trainee.id]})`
                 : ''}
           </option>
         ))}
       </select>
     </div>
     <div className="mb-6">
       <label className="block mb-2 font-semibold">Select FTO:</label>
       <select
         className={inputStyle}
         value={selectedTrainerOrAdmin}
         onChange={(e) => setSelectedTrainerOrAdmin(e.target.value)}
       >
         <option value="">Choose a trainer or admin</option>
         {trainersAndAdmins.map((user) => (
           <option key={user.id} value={user.id}>
             {user.name} (FTO)
           </option>
         ))}
       </select>
     </div>
     <div className="space-y-2">
       {selectedTrainerOrAdmin && !isTrainerAssignedToTrainee && (
         <button
           className={`${buttonStyle} bg-green-500 hover:bg-green-600`}
           onClick={handleAssign}
           disabled={!isSelectedTraineeActive || !isSelectedTrainerActive}
         >
           <FiUserCheck className="mr-2" />
           Assign Trainer/Admin
         </button>
       )}
       {isTrainerAssignedToTrainee && (
         <button
           className={`${buttonStyle} bg-red-500 hover:bg-red-600`}
           onClick={handleUnassign}
           disabled={!isSelectedTraineeActive || !isSelectedTrainerActive}
         >
           <FiUserMinus className="mr-2" />
           Unassign Trainer/Admin
         </button>
       )}
       {selectedTrainee && (
         <>
           <button
             className={`${buttonStyle} bg-blue-500 hover:bg-blue-600`}
             onClick={() => setShowAssignmentHistory(true)}
           >
             <MdHistory className="mr-2" />
             View Assignment History
           </button>
           <button
             className={`${buttonStyle} bg-purple-500 hover:bg-purple-600 ${
               loading ? 'opacity-50 cursor-not-allowed' : ''
             }`}
             onClick={handleDownloadAssignmentHistory}
             disabled={loading}
           >
             {loading ? (
               <>
                 <svg
                   className="animate-spin h-5 w-5 mr-2 text-white"
                   xmlns="http://www.w3.org/2000/svg"
                   fill="none"
                   viewBox="0 0 24 24"
                 >
                   <circle
                     className="opacity-25"
                     cx="12"
                     cy="12"
                     r="10"
                     stroke="currentColor"
                     strokeWidth="4"
                   ></circle>
                   <path
                     className="opacity-75"
                     fill="currentColor"
                     d="M4 12a8 8 0 018-8v8H4z"
                   ></path>
                 </svg>
                 Generating PDF...
               </>
             ) : (
               <>
                 <MdHistory className="mr-2" />
                 Download Assignment History
               </>
             )}
           </button>
         </>
       )}
     </div>
     <div className="mt-6">
       <h3 className="font-bold mb-2 flex items-center">
         <FiUsers className="mr-2" />
         Current Assignments:
       </h3>
       <ul className="space-y-2">
         {Object.entries(currentAssignments).map(([traineeId, assignedTo]) => (
           <li key={traineeId} className="text-sm flex items-center">
             <FiUser className="mr-2" />
             {trainees.find((t) => t.id === traineeId)?.name} -{' '}
             {assignedTo || 'N/A'}
           </li>
         ))}
       </ul>
     </div>

     {/* Assignment History Modal */}
     {showAssignmentHistory && selectedTrainee && (
       <AssignmentHistory
         traineeId={selectedTrainee}
         onClose={() => setShowAssignmentHistory(false)}
       />
     )}

     <ConfirmationModal
       ref={modalRef}
       isOpen={confirmModal.isOpen}
       onClose={closeConfirmModal}
       onConfirm={() => {
         confirmModal.onConfirm();
         closeConfirmModal();
       }}
       title={confirmModal.title}
       message={confirmModal.message}
     />
   </div>
 );
};

export default AssignTrainee;
